/**
 * Runtime Config Source Banner
 *
 * Explains where the currently-viewed config lives: global defaults, an
 * inherited copy (editing forks one at this level), or a copy already owned
 * at this level (Revert drops it and falls back to inheriting again).
 * Extracted from RuntimeConfigDialog to keep it under the 200-line cap.
 */

import type { ReactElement } from 'react';

interface RuntimeConfigSourceBannerProps {
  contextLevel: 'global' | 'pack' | 'project';
  ownedScope: 'global' | 'pack' | 'project';
  ownedByContext: boolean;
  packName?: string;
  projectName?: string;
}

export function RuntimeConfigSourceBanner({
  contextLevel,
  ownedScope,
  ownedByContext,
  packName,
  projectName,
}: RuntimeConfigSourceBannerProps): ReactElement {
  let text: string;
  if (contextLevel === 'global') {
    text = 'Global defaults — apply to every project unless a pack or project owns its own copy.';
  } else if (ownedByContext) {
    const where = contextLevel === 'pack' ? (packName ?? 'this pack') : (projectName ?? 'this project');
    text = `Owned by ${where}. Edits apply here only. Use Revert to drop this copy and inherit again.`;
  } else {
    text = `Inherited from ${ownedScope}. Editing forks an independent copy at the ${contextLevel} level.`;
  }
  return (
    <div className="admin-page__sync-message runtime-config__scope-banner" role="status">
      {text}
    </div>
  );
}
